import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ListChecks, MagnifyingGlass } from "@phosphor-icons/react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");

  const load = async (q = "") => {
    try {
      const params = q ? { search: q } : {};
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data);
    } catch {
      toast.error("Failed to load audit logs");
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Operations Trail" title="AUDIT LOGS">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border border-app-border text-app-subtle bg-app-surface">
          <ListChecks size={12} />
          {logs.length} entries
        </span>
      </PageHeader>

      <form onSubmit={handleSearch} className="page-filter-bar mb-6">
        <div className="page-filter-field flex-1 min-w-0">
          <label htmlFor="audit-search" className="page-filter-label">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none" />
            <input
              id="audit-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Actions, users, emails…"
              className="w-full bg-app-bg border border-app-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-app-accent min-h-[40px]"
              data-testid="audit-search"
            />
          </div>
        </div>
        <div className="page-filter-field w-full sm:w-36 lg:w-40">
          <span className="page-filter-label">&nbsp;</span>
          <button
            type="submit"
            className="w-full min-h-[40px] px-3 border border-app-border bg-app-bg font-mono-d text-xs uppercase tracking-widest text-app-text hover:bg-app-hover transition-colors"
          >
            Search
          </button>
        </div>
      </form>

      <div className="border border-app-border bg-app-surface" data-testid="audit-list">
        {logs.length === 0 && <div className="p-8 text-center font-mono-d text-xs text-app-muted">NO LOGS</div>}
        {logs.map(l => (
          <div key={l.id} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 px-4 py-3 border-b border-app-border/70">
            <ListChecks size={16} className="text-[#007AFF] mt-0.5 shrink-0 hidden sm:block" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <ListChecks size={16} className="text-[#007AFF] sm:hidden shrink-0" />
                <span className="font-mono-d text-sm break-all">{l.user_email}</span>
                <span className="font-mono-d text-[10px] text-app-muted uppercase">[{l.user_role}]</span>
              </div>
              <div className="text-sm text-app-text/80 break-words mt-1">
                <span className="font-mono-d text-[#007AFF]">{l.action}</span>
                {l.account_id && <span className="text-app-muted"> · account {l.account_id.slice(0, 8)}</span>}
                {l.details && Object.keys(l.details).length > 0 && (
                  <span className="text-app-subtle font-mono-d text-xs block sm:inline mt-1 sm:mt-0 break-all"> · {JSON.stringify(l.details)}</span>
                )}
              </div>
            </div>
            <div className="font-mono-d text-[10px] text-app-muted sm:whitespace-nowrap shrink-0">{new Date(l.timestamp).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}
